import { readScrollPosition, saveScrollPosition } from '@shared-core/navigation'
import { type Ref, useCallback, useLayoutEffect, useRef, useState } from 'react'
import { type NativeScrollEvent, type NativeSyntheticEvent, Platform } from 'react-native'

// Восстановление позиции скролла inner-контейнера при возврате назад (web/SPA, на native no-op).
// react-router <ScrollRestoration> умеет только window (remix-run/react-router#9495), а у нас
// скроллит внутренний контейнер (bounce/виртуализация) — ведём позицию сами по ключу истории.
// Контент истории догружается лениво: досдвигаем позицию по мере роста контента (ResizeObserver),
// под лоадером (isRestoring), пока не дотянемся до сохранённого оффсета.
const IS_WEB = Platform.OS === 'web'
const RESTORE_TIMEOUT_MS = 1000

// Структурные типы DOM + доступ к ResizeObserver через globalThis: файл компилится и под native
// tsconfig (без dom-lib). Реально исполняется только на web (IS_WEB), под native — no-op.
type ScrollNode = {
  scrollTop: number
  scrollHeight: number
  clientHeight: number
  firstElementChild: ScrollNode | null
}
type ResizeObserverLike = { observe: (target: ScrollNode) => void; disconnect: () => void }
const ResizeObserverCtor = (globalThis as unknown as { ResizeObserver?: new (cb: () => void) => ResizeObserverLike })
  .ResizeObserver

type ScrollInstance = { getScrollableNode?: () => ScrollNode | null }
type Result<T> = { ref: Ref<T>; onScroll: (e: NativeSyntheticEvent<NativeScrollEvent>) => void; isRestoring: boolean }

export function useScrollRestoration<T>(scopeId: string, forwardedRef?: Ref<T>): Result<T> {
  const instanceRef = useRef<ScrollInstance | null>(null)
  const restoringRef = useRef(false) // блокирует сохранение, пока наши scrollTop сами триггерят onScroll
  const [isRestoring, setIsRestoring] = useState(() => IS_WEB && Boolean(readScrollPosition(scopeId)))

  const ref = useCallback(
    (value: T | null) => {
      instanceRef.current = value as ScrollInstance | null

      if (typeof forwardedRef === 'function') forwardedRef(value)
      else if (forwardedRef) (forwardedRef as { current: T | null }).current = value
    },
    [forwardedRef],
  )

  useLayoutEffect(() => {
    const target = IS_WEB ? readScrollPosition(scopeId) : undefined
    const node = instanceRef.current?.getScrollableNode?.()

    if (!target || !node) {
      setIsRestoring(false)
      return
    }

    restoringRef.current = true
    setIsRestoring(true)

    let observer: ResizeObserverLike | null = null

    const finish = () => {
      restoringRef.current = false
      setIsRestoring(false)
      observer?.disconnect()
      clearTimeout(timer)
    }
    const apply = () => {
      node.scrollTop = target

      if (node.scrollHeight - node.clientHeight >= target) finish() // контент дорос до цели
    }

    // Предохранитель: контент так и не дорос (короче оффсета) — не держим лоадер вечно.
    const timer = setTimeout(finish, RESTORE_TIMEOUT_MS)

    // Наблюдаем контент (растёт при догрузке), а не вьюпорт (его высота фиксирована).
    observer = ResizeObserverCtor ? new ResizeObserverCtor(apply) : null
    observer?.observe(node.firstElementChild ?? node)
    apply()

    return finish
  }, [scopeId])

  const onScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (IS_WEB && !restoringRef.current) saveScrollPosition(scopeId, event.nativeEvent.contentOffset.y)
  }

  return { ref, onScroll, isRestoring }
}
